<template>
  <div id="provider-profile">
    <poppins-notification :message="notification" :triggerNotification="showNotification" class="mt-16" />
    <provider-profile-error
      v-if="generalErrorType"
      :errorType="generalErrorType"
      :viewerAccountData="accountData"
      :paramData="paramData"
    />
    <div v-else>
      <provider-profile-banner v-if="bannerType" :bannerType="bannerType" @toggle-profile-visibility="toggleProfileVisibility" />
      <provider-profile-container
        :profileAccountData="profileAccountData"
        :viewerAccountData="accountData"
        :reviewsData="reviewsData"
        :loading="loading"
        :paramData="paramData"
        :accountTypeInfo="accountTypeInfo"
        :editModeActive="editModeActive"
        :previewModeActive="previewModeActive"
        :bannerType="bannerType"
        :isProfileOwner="isProfileOwner"
        @toggle-profile-visibility="toggleProfileVisibility"
        @show-preview-mode="showPreviewMode"
        @remove-preview-mode="removePreviewMode"
        @view-more-reviews="handleViewMoreReviews"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { mapGetters } from 'vuex';
import { BannerType } from './__types__/BannerTypes';
import { GeneralErrorType } from './__types__/ErrorTypes';
import { FormattedCareType } from './__types__/CareTypes';
import ProviderProfileError from './components/ProviderProfileError.vue';
import ProviderProfileBanner from './components/ProviderProfileBanner.vue';
import ProviderProfileContainer from './components/ProviderProfileContainer.vue';
import { profileQueryMap, FETCH_REVIEWS, VIEWER_HAS_MESSAGED } from '@/graphql/provider/profile/queries';
import { UPDATE_SITTER_PROFILE } from '@/graphql/account/provider/mutations';
import PoppinsNotification from '@/components/notification/Notification.vue';
import { setRecentlyViewedProviderId } from '@/lib/recentlyViewedProfile';

export default {
  name: 'ProviderProfile',
  components: {
    ProviderProfileError,
    ProviderProfileBanner,
    ProviderProfileContainer,
    PoppinsNotification,
  },
  data(): any {
    return {
      generalErrorType: GeneralErrorType.NONE,
      bannerType: BannerType.NONE,
      profileAccountData: null,
      reviewsData: null,
      loading: true,
      reviewsFetchCount: 3,
      notification: '',
      showNotification: false,
    };
  },
  computed: {
    ...mapGetters('account', ['getAccount']),
    accountData() {
      return this.getAccount;
    },
    paramData() {
      return {
        careType: this.$route.params.care_type,
        sitterUuid: this.$route.params.sitter_uuid,
      };
    },
    accountTypeInfo() {
      return {
        isSitter: this.accountData?.isSitter?.() || false,
        isParent: this.accountData?.isParent?.() || false,
        isBasic: this.accountData?.isBasic?.() || false,
        accountType: this.accountData?.accountType?.() || '',
      };
    },
    formattedCareType() {
      return FormattedCareType[this.paramData.careType.toUpperCase()];
    },
    careTypeProfile() {
      return this.profileAccountData?.profiles?.[this.formattedCareType] || {};
    },
    isProfileOwner() {
      return this.profileAccountData?.id === this.accountData?.id;
    },
    careTypeDisplay() {
      return this.paramData.careType.replace('_', ' ');
    },
    editModeActive(): boolean {
      return this.isProfileOwner && !this.previewModeActive;
    },
    previewModeActive(): boolean {
      return this.isProfileOwner && this.$route.query.preview_mode === 'true';
    },
  },
  watch: {
    '$route.params.care_type': {
      handler() {
        this.fetchProfileData();
      },
    },
    '$route.params.sitter_uuid': {
      handler() {
        this.fetchProfileData();
        if (this.accountTypeInfo?.isBasic && this.paramData.sitterUuid && this.paramData.sitterUuid !== this.accountData?.id) {
          setRecentlyViewedProviderId(this.paramData.sitterUuid);
        }
      },
    },
    '$route.query.preview_mode': {
      handler() {
        this.fetchProfileData();
      },
    },
  },
  mounted() {
    this.fetchProfileData();
    this.fetchReviews();
    this.fetchViewerHasMessaged();
    if (this.accountTypeInfo?.isBasic && this.paramData.sitterUuid && this.paramData.sitterUuid !== this.accountData?.id) {
      setRecentlyViewedProviderId(this.paramData.sitterUuid);
    }
    this._onVisibilityChange = () => {
      if (this.editModeActive) this.fetchProfileData();
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
  },
  methods: {
    updateBannerState() {
      if (!this.profileAccountData?.profiles || !this.isProfileOwner) {
        this.bannerType = BannerType.NONE;
        return;
      }
      const { profileVisible, profileCompleted } = this.careTypeProfile;

      if (!profileCompleted) {
        this.generalErrorType = GeneralErrorType.PROFILE_INCOMPLETE;
        this.bannerType = BannerType.NONE;
      } else if (!profileVisible) {
        this.bannerType = BannerType.PROFILE_HIDDEN;
      } else if (profileVisible && this.previewModeActive) {
        this.bannerType = BannerType.PROFILE_PREVIEW_MODE;
      } else if (profileVisible) {
        this.bannerType = BannerType.PROFILE_EDIT_MODE;
      } else {
        this.bannerType = BannerType.NONE;
      }
    },
    handleViewMoreReviews(): void {
      this.reviewsFetchCount += 3;
      this.fetchReviews();
    },
    async fetchProfileData() {
      this.loading = true;
      this.generalErrorType = GeneralErrorType.NONE;

      try {
        if (!this.validateCareTypeQuery(this.paramData.careType)) {
          return;
        }

        const data = await this.executeProfileQuery(this.paramData.careType);
        this.processProfileData(data);
      } catch (error) {
        this.handleProfileErrors(error);
      } finally {
        this.loading = false;
      }
    },
    validateCareTypeQuery(careType: string): boolean {
      const careTypeQuery = profileQueryMap[careType.toLowerCase()];

      if (!careTypeQuery) {
        this.generalErrorType = GeneralErrorType.DEFAULT;
        console.error(`No query found for care type: ${careType}`);
        return false;
      }

      return true;
    },
    async executeProfileQuery(careType: string) {
      const careTypeQuery = profileQueryMap[careType.toLowerCase()];

      const { data } = await this.$apollo.query({
        query: careTypeQuery,
        variables: {
          accountId: this.paramData.sitterUuid,
        },
        fetchPolicy: this.isProfileOwner ? 'network-only' : 'cache-first',
      });

      return data;
    },
    processProfileData(data: any) {
      this.profileAccountData = { ...this.profileAccountData, ...data.account };

      if (!this.validateProfileExists(this.careTypeProfile)) {
        return;
      }

      if (!this.validateProfileVisibility(this.careTypeProfile, this.isProfileOwner)) {
        return;
      }

      this.updateBannerState();
    },
    validateProfileExists(careTypeProfile: any): boolean {
      // A profile is considered unavailable if it does not exist or the profile object is missing
      if (!careTypeProfile || !careTypeProfile.profile) {
        this.generalErrorType = GeneralErrorType.PROFILE_UNAVAILABLE;
        this.bannerType = BannerType.NONE;
        return false;
      }
      return true;
    },
    validateProfileVisibility(careTypeProfile: any, isProfileOwner: boolean): boolean {
      // For owners, prevent viewing preview mode if profile is hidden
      if (isProfileOwner && !careTypeProfile.profileVisible) {
        if (this.previewModeActive) this.removePreviewMode();
      }
      // For non-owners, if showProfile is false, show error
      if (!isProfileOwner && !careTypeProfile.showProfile) {
        this.generalErrorType = GeneralErrorType.PROFILE_UNAVAILABLE;
        this.bannerType = BannerType.NONE;
        return false;
      }
      return true;
    },
    handleProfileErrors(error: any) {
      console.error(error);

      // Show "profile unavailable" error if the account is not found (e.g. closed, termed)
      if (error.message.includes('Account not found')) {
        this.generalErrorType = GeneralErrorType.PROFILE_UNAVAILABLE;
        return;
      }

      // Show default error for any other errors
      this.generalErrorType = GeneralErrorType.DEFAULT;
    },
    async fetchViewerHasMessaged() {
      try {
        const { data } = await this.$apollo.query({
          query: VIEWER_HAS_MESSAGED,
          variables: {
            accountId: this.paramData.sitterUuid,
          },
        });
        this.profileAccountData = {
          ...this.profileAccountData,
          viewerHasMessaged: data.account.viewerHasMessaged,
        };
        return data.account.viewerHasMessaged;
      } catch (error) {
        console.error('Error fetching viewer has messaged:', error);
        return false;
      }
    },
    async fetchReviews() {
      try {
        const { data } = await this.$apollo.query({
          query: FETCH_REVIEWS,
          variables: {
            accountId: this.paramData.sitterUuid,
            reviewsFetchCount: this.reviewsFetchCount,
          },
        });
        this.reviewsData = data.account.reviews;
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    },
    async toggleProfileVisibility(afterUnhidePreview = false) {
      const { careType } = this.paramData;
      this.showNotification = false;

      try {
        await this.$apollo.mutate({
          mutation: UPDATE_SITTER_PROFILE,
          variables: {
            input: {
              sitterToggleProfile: {
                careType: careType.toUpperCase(),
              },
            },
          },
        });

        // Handle "unhide & preview" action
        if (afterUnhidePreview) this.showPreviewMode();

        // Refetch profile data with fetchPolicy set to 'network-only' to avoid stale cache
        await this.fetchProfileData();

        if (this.careTypeProfile.profileVisible) {
          this.notification = `Your ${this.careTypeDisplay} profile is now visible to parents`;
          this.showNotification = true;
        }
      } catch (error) {
        console.error('Error toggling profile visibility:', error);

        // Show user-friendly error message
        this.notification = 'Failed to update profile. Please try again.';
        this.showNotification = true;
      }
    },
    showPreviewMode() {
      // Add preview_mode param, showing preview view
      const { path, query } = this.$route;
      this.$router.push({ path, query: { ...query, preview_mode: 'true' } });
    },
    removePreviewMode() {
      // Remove preview_mode param, showing default (edit) view
      const { path, query } = this.$route;
      const { preview_mode: previewMode, ...rest } = query;
      this.$router.push({ path, query: rest });
    },
  },
};
</script>

<style lang="scss">
main:has(#provider-profile) ~ #onetrust-consent-sdk #ot-sdk-btn-floating {
  z-index: 1;
}

main:has(#provider-profile) .condensed-footer {
  display: none; // hide on mobile so does not interfere with action button

  @media (min-width: 768px) {
    display: block;
  }
}
</style>
